import { ChatRoom, SendMessageRequest } from "amazon-ivs-chat-messaging";
import { postJson, RequestError } from "utils/json_request";
import { ChunkAssembler, normalizeMessage, normalizeEvent, normalizeDelete, sessionToToken } from "live_chat/normalize";
import { jitter, expBackoff, sleep } from "live_chat/backoff";

// One IVS Chat room connection per live event per page, shared by every controller that needs it
// (the chat panel, and the console's poll column through the panel's window events). The client
// owns the SDK ChatRoom, the session POST that mints its token, all reconnect pacing, and the
// presence heartbeat; the panel only consumes the frames it emits.
//
// The SDK's own retry loop (maxReconnectAttempts) has no delay between attempts and calls
// tokenProvider for each, so every wait lives inside tokenProvider - the one place that can hold
// an attempt back without the SDK counting it.

// A Turbo replace (viewer go-live refresh) or morph (console) disconnects the panel controller
// and reconnects it a moment later on the same permanent element; the grace keeps the room alive
// across that gap so the remount re-attaches with no new token.
const RELEASE_GRACE_MS = 2000;
const SDK_RECONNECT_ATTEMPTS = 5;
// Cycles of SDK attempts before the client stops and waits for a manual connect().
const MAX_BACKOFF_CYCLES = 6;
// Three clean server closes inside a minute stop looking like moderation kicks and start looking
// like an edge that keeps dropping us, so the client backs off instead of hammering it.
const SERVER_DISCONNECT_BURST = 3;
const SERVER_DISCONNECT_WINDOW_MS = 60000;
// iOS leaves sockets half-open after this long in the background; refresh once instead of trusting it.
const HIDDEN_REFRESH_MS = 60000;
// A connection counts as healthy only once it has stayed up this long; until then a reconnect
// keeps escalating the backoff, so an edge that accepts the socket and drops it cannot hold the
// client at cycle one, minting a CreateChatToken per drop against the fixed 200 TPS.
const STABLE_CONNECTION_MS = 30000;
// Consecutive tokens minted without a stable connection in between before the client gives up.
const MAX_FAST_MINTS = 8;
const TERMINAL_STATUSES = { 401: "unauthenticated", 403: "forbidden", 410: "room_closed" };

const registry = new Map();

class RoomClient {
  constructor({ key, contextType, contextId, endpoints, heartbeatIntervalMs }) {
    this.key = key;
    this.contextType = contextType;
    this.contextId = contextId;
    this.endpoints = endpoints;
    this.heartbeatIntervalMs = heartbeatIntervalMs;

    this.refcount = 0;
    this.listeners = new Map();
    this.room = null;
    // "idle" | "connecting" | "connected" | "backoff" | "gaveUp" | "closed"
    this.phase = "idle";
    this.lastBootstrap = null;
    // The session fetched by connect() before the ChatRoom exists; tokenProvider hands it over
    // once so the single-use token is not minted twice on the first connect.
    this.primedBootstrap = null;
    this.booting = false;
    this.refreshing = false;
    this.terminalReason = null;
    this.disposed = false;
    this.abort = new AbortController();

    this.sessionFailures = 0;
    this.lastSessionFailureAt = 0;
    this.lastBootstrapAt = 0;
    this.fastMints = 0;
    this.connectionStable = false;
    this.stableTimer = null;
    this.backoffCycle = 0;
    this.serverDisconnectsAt = [];
    this.reconnectTimer = null;
    this.releaseTimer = null;
    this.hiddenAt = 0;
    this.beating = false;
    this.chunks = new ChunkAssembler();

    this.onVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        this.hiddenAt = Date.now();
        return;
      }
      const hiddenFor = this.hiddenAt ? Date.now() - this.hiddenAt : 0;
      this.hiddenAt = 0;
      this.wake(hiddenFor);
      // Not while a refresh is under way: its bootstrap brings the fresh presence ticket.
      if (!this.refreshing) this.beat();
    };
    this.onWake = () => this.wake(0);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("pageshow", this.onWake);
    window.addEventListener("online", this.onWake);

    // Presence means watching, not chatting, so this ticks regardless of the socket state.
    this.heartbeatTimer = window.setInterval(() => this.beat(), heartbeatIntervalMs);
  }

  // --- public surface -----------------------------------------------------

  get state() {
    return this.phase;
  }

  on(type, fn) {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type).add(fn);
    return () => {
      const set = this.listeners.get(type);
      if (set) set.delete(fn);
    };
  }

  // Idempotent: a no-op while connecting or connected. After gaveUp, or during a backoff wait,
  // it resets the pacing and connects now.
  connect() {
    if (this.disposed || this.terminalReason) return;

    this.clearReconnectTimer();
    if (this.phase === "gaveUp" || this.phase === "backoff") {
      this.backoffCycle = 0;
      this.fastMints = 0;
    }

    if (this.room) {
      if (this.room.state === "disconnected") this.openRoom();
      return;
    }
    if (this.booting) return;

    this.booting = true;
    this.setPhase("connecting");
    this.fetchSession()
      .then((bootstrap) => {
        if (this.disposed || this.terminalReason) return;
        this.room = this.buildRoom(bootstrap.region);
        this.primedBootstrap = bootstrap;
        this.openRoom();
      })
      .catch(() => {
        // Terminal statuses have already emitted closed; anything else is a failed cycle.
        if (!this.disposed && !this.terminalReason) this.onDisconnect("fetchTokenError");
      })
      .finally(() => {
        this.booting = false;
      });
  }

  // Resolves with the SDK's ChatMessage (our own echo) or rejects with the SDK's ChatError; the
  // SDK's synchronous throws (not connected, duplicate request) surface as rejections too.
  send(body, { clientMsgId } = {}) {
    try {
      if (!this.room || this.room.state !== "connected") return Promise.reject(new Error("Room is not connected"));
      const attributes = clientMsgId ? { client_msg_id: String(clientMsgId) } : undefined;
      return this.room.sendMessage(new SendMessageRequest(body, attributes));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // The panel's own vote ack comes back over HTTP, not through the room, so it is folded into the
  // cached bootstrap here for a later remount replay.
  noteVote(pollId, optionId) {
    const state = this.lastBootstrap && this.lastBootstrap.state;
    if (!state || !state.poll || !state.poll.poll) return;
    if (Number(state.poll.poll.id) !== Number(pollId)) return;
    state.poll.your_option_id = optionId;
  }

  // Disconnect and reconnect with a fresh token (and a fresh bootstrap: new capabilities after a
  // timeout expires, new viewer flags after a kick). Callers rate-limit this.
  refresh() {
    if (this.disposed || this.terminalReason) return;

    if (!this.room || this.room.state === "disconnected") {
      this.connect();
      return;
    }
    this.refreshing = true;
    this.room.disconnect();
  }

  acquire() {
    this.refcount += 1;
    if (this.releaseTimer) {
      window.clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }
  }

  release() {
    this.refcount = Math.max(0, this.refcount - 1);
    if (this.refcount > 0 || this.releaseTimer) return;

    this.releaseTimer = window.setTimeout(() => {
      this.releaseTimer = null;
      if (this.refcount === 0) this.dispose();
    }, RELEASE_GRACE_MS);
  }

  // --- session / token provider -------------------------------------------

  buildRoom(region) {
    const room = new ChatRoom({
      regionOrUrl: region,
      tokenProvider: () => this.tokenProvider(),
      maxReconnectAttempts: SDK_RECONNECT_ATTEMPTS,
      id: this.key,
    });
    room.logLevel = "error";
    room.addListener("connecting", () => {
      this.markUnstable();
      this.setPhase("connecting");
    });
    room.addListener("connect", () => {
      this.markUnstable();
      // The pacing counters reset only once the connection has proven itself, never on connect.
      this.stableTimer = window.setTimeout(() => {
        this.connectionStable = true;
        this.backoffCycle = 0;
        this.fastMints = 0;
        this.serverDisconnectsAt = [];
      }, STABLE_CONNECTION_MS);
      this.setPhase("connected");
    });
    room.addListener("disconnect", (reason) => {
      this.markUnstable();
      this.onDisconnect(reason);
    });
    room.addListener("message", (message) => this.emit("message", normalizeMessage(message)));
    room.addListener("event", (event) => {
      const frame = normalizeEvent(event, this.chunks);
      if (frame) this.emitEvent(frame);
    });
    room.addListener("messageDelete", (event) => this.emit("remove", normalizeDelete(event)));
    // userDisconnect is ignored on purpose: a ban purges rows through our own user_removed event.
    return room;
  }

  async tokenProvider() {
    if (this.primedBootstrap) {
      const bootstrap = this.primedBootstrap;
      this.primedBootstrap = null;
      return sessionToToken(bootstrap);
    }
    return sessionToToken(await this.fetchSession());
  }

  // The one place a session is requested. 429 waits Retry-After and loops without consuming an
  // SDK attempt; 401/403/410 are terminal; a network error or 5xx rejects, which the SDK counts.
  async fetchSession() {
    for (;;) {
      if (this.disposed) throw new Error("disposed");
      if (this.terminalReason) throw new Error(`closed:${this.terminalReason}`);

      // The SDK retries with no delay, so consecutive failures are spaced here.
      if (this.lastSessionFailureAt && Date.now() - this.lastSessionFailureAt < 1000) {
        await sleep(expBackoff(this.sessionFailures - 1), this.abort.signal);
        if (this.disposed) throw new Error("disposed");
      }
      // ... and so are re-mints that follow a token whose connection never became stable.
      if (this.lastBootstrapAt && !this.connectionStable) {
        this.fastMints += 1;
        if (this.fastMints > MAX_FAST_MINTS) {
          this.giveUp();
          throw new Error("gave up");
        }
        await sleep(expBackoff(this.fastMints - 1), this.abort.signal);
        if (this.disposed) throw new Error("disposed");
      }

      try {
        const bootstrap = await postJson(this.endpoints.session, this.contextParams());
        this.sessionFailures = 0;
        this.lastSessionFailureAt = 0;
        this.lastBootstrapAt = Date.now();
        this.lastBootstrap = bootstrap;
        this.emit("bootstrap", bootstrap);
        return bootstrap;
      } catch (error) {
        const status = error instanceof RequestError ? error.status : 0;

        if (status === 429) {
          this.sessionFailures += 1;
          await sleep(jitter(error.retryAfterMs || expBackoff(this.sessionFailures - 1), 1000), this.abort.signal);
          continue;
        }
        if (TERMINAL_STATUSES[status]) {
          this.close(TERMINAL_STATUSES[status]);
          throw error;
        }

        this.sessionFailures += 1;
        this.lastSessionFailureAt = Date.now();
        throw error;
      }
    }
  }

  // --- reconnect policy ---------------------------------------------------

  onDisconnect(reason) {
    if (this.disposed || this.terminalReason) return;

    if (reason === "clientDisconnect") {
      if (!this.refreshing) {
        this.setPhase("disconnected");
        return;
      }
      this.refreshing = false;
      this.setPhase("connecting");
      this.scheduleOpen(0);
      return;
    }

    if (reason === "serverDisconnect") {
      // A moderation kick and a WebView's clean close look identical here; the next bootstrap
      // says whether we came back read-only, shadowed or timed out.
      const now = Date.now();
      this.serverDisconnectsAt = this.serverDisconnectsAt.filter((at) => now - at < SERVER_DISCONNECT_WINDOW_MS);
      this.serverDisconnectsAt.push(now);
      if (this.serverDisconnectsAt.length < SERVER_DISCONNECT_BURST) {
        this.setPhase("connecting");
        this.scheduleOpen(jitter(250, 1250));
        return;
      }
    }

    // socketError / fetchTokenError (the SDK's attempts are spent) or a burst of server closes.
    this.backoffCycle += 1;
    if (this.backoffCycle > MAX_BACKOFF_CYCLES) {
      this.setPhase("gaveUp");
      this.emit("gaveUp");
      return;
    }
    this.setPhase("backoff");
    this.scheduleOpen(expBackoff(this.backoffCycle - 1));
  }

  // Always asynchronous: the SDK dispatches `disconnect` before it disposes the closing
  // connection, so a connect() re-entered from the listener would be torn down immediately.
  scheduleOpen(delayMs) {
    this.clearReconnectTimer();
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.openRoom();
    }, delayMs);
  }

  openRoom() {
    if (this.disposed || this.terminalReason) return;
    if (!this.room) {
      this.connect();
      return;
    }
    if (this.room.state !== "disconnected") return;
    this.room.connect();
  }

  // Foreground / pageshow / online: reconnect at once if we are down or waiting, and refresh a
  // connection that sat hidden long enough for the socket to have died underneath us.
  wake(hiddenForMs) {
    if (this.disposed || this.terminalReason) return;

    const down = this.phase === "gaveUp" || this.phase === "backoff" || this.phase === "disconnected"
      || (this.room && this.room.state === "disconnected" && !this.booting);
    if (down) {
      this.backoffCycle = 0;
      this.connect();
      return;
    }
    if (this.phase === "connected" && hiddenForMs > HIDDEN_REFRESH_MS) this.refresh();
  }

  // --- presence -----------------------------------------------------------

  async beat() {
    if (this.disposed || this.terminalReason || this.beating) return;
    const ticket = this.lastBootstrap && this.lastBootstrap.presence_ticket;
    if (!ticket) return;

    this.beating = true;
    try {
      const response = await postJson(this.endpoints.heartbeat, { presence_ticket: ticket });
      if (response && typeof response.viewers === "number") this.emitEvent({ type: "viewers", count: response.viewers });
    } catch (error) {
      const status = error instanceof RequestError ? error.status : 0;
      // 410: the room has closed under us. 401: the ticket expired (a session lasts 180 min and
      // a locked phone can sleep past it), so a fresh session, not a closed panel. 403: not ours.
      if (status === 410) this.close("room_closed");
      else if (status === 401 && !this.refreshing) this.refresh();
      else if (status === 403) this.close("forbidden");
    } finally {
      this.beating = false;
    }
  }

  // Server events fold into the cached bootstrap so a remount replay (see the panel) restores the
  // room as it is now, not as it was at join time.
  emitEvent(frame) {
    this.foldIntoBootstrap(frame);
    this.emit("event", frame);
  }

  foldIntoBootstrap(frame) {
    const bootstrap = this.lastBootstrap;
    if (!bootstrap) return;
    const room = bootstrap.room || (bootstrap.room = {});
    const state = bootstrap.state || (bootstrap.state = {});

    switch (frame.type) {
      case "settings":
        room.slow_mode_seconds = frame.slow_mode_seconds;
        break;
      case "announcement":
        room.announcement = frame.body && frame.body.trim() !== "" ? { body: frame.body, body_html: frame.body_html } : null;
        break;
      case "viewers":
        state.viewers = frame.count;
        break;
      case "poll":
        if (frame.action === "cancelled" || frame.action === "dismiss") {
          state.poll = null;
        } else if (frame.poll) {
          const same = state.poll && state.poll.poll && Number(state.poll.poll.id) === Number(frame.poll.id);
          state.poll = { poll: frame.poll, your_option_id: same ? state.poll.your_option_id : null };
        }
        break;
      case "tip": {
        const tips = (state.tips || []).filter((tip) => !frame.tip || Number(tip.id) !== Number(frame.tip.id));
        state.tips = frame.action === "created" && frame.tip ? [frame.tip, ...tips] : tips;
        break;
      }
      case "user_removed":
        state.tips = (state.tips || []).filter((tip) => !tip.user || String(tip.user.id) !== String(frame.user_id));
        break;
    }
  }

  // --- lifecycle ----------------------------------------------------------

  markUnstable() {
    this.connectionStable = false;
    if (this.stableTimer) window.clearTimeout(this.stableTimer);
    this.stableTimer = null;
  }

  // Too many tokens without a connection that lasted: stop until a manual connect().
  giveUp() {
    this.clearReconnectTimer();
    if (this.room && this.room.state !== "disconnected") this.room.disconnect();
    this.setPhase("gaveUp");
    this.emit("gaveUp");
  }

  close(reason) {
    if (this.disposed || this.terminalReason) return;

    this.terminalReason = reason;
    this.clearReconnectTimer();
    this.markUnstable();
    this.stopHeartbeat();
    if (this.room && this.room.state !== "disconnected") this.room.disconnect();
    this.phase = "closed";
    this.emit("closed", { reason });
  }

  dispose() {
    if (this.disposed) return;

    this.disposed = true;
    this.abort.abort();
    this.clearReconnectTimer();
    this.markUnstable();
    this.stopHeartbeat();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("pageshow", this.onWake);
    window.removeEventListener("online", this.onWake);
    if (this.room && this.room.state !== "disconnected") this.room.disconnect();
    this.room = null;
    this.listeners.clear();
    if (registry.get(this.key) === this) registry.delete(this.key);
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) window.clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  contextParams() {
    return { context_type: this.contextType, context_id: this.contextId };
  }

  setPhase(phase) {
    if (this.phase === phase || this.terminalReason) return;
    this.phase = phase;
    this.emit("state", phase);
  }

  emit(type, payload) {
    const set = this.listeners.get(type);
    if (!set) return;
    Array.from(set).forEach((fn) => {
      try {
        fn(payload);
      } catch (error) {
        // One listener's failure must not starve the others of the same frame.
        window.setTimeout(() => { throw error; });
      }
    });
  }
}

// What a controller holds: its own listener list (released together) over the shared client.
class RoomHandle {
  constructor(client) {
    this.client = client;
    this.unsubscribers = [];
    client.acquire();
  }

  get state() {
    return this.client.state;
  }

  get lastBootstrap() {
    return this.client.lastBootstrap;
  }

  // How old lastBootstrap is, so a replay can age its time-bound fields (a timeout's seconds left).
  get bootstrapAgeMs() {
    return this.client.lastBootstrapAt ? Date.now() - this.client.lastBootstrapAt : 0;
  }

  on(type, fn) {
    const unsubscribe = this.client.on(type, fn);
    this.unsubscribers.push(unsubscribe);
    return unsubscribe;
  }

  connect() {
    this.client.connect();
  }

  send(body, options) {
    return this.client.send(body, options);
  }

  refresh() {
    this.client.refresh();
  }

  noteVote(pollId, optionId) {
    this.client.noteVote(pollId, optionId);
  }

  release() {
    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = [];
    this.client.release();
  }
}

export const LiveRoomClient = {
  acquire({ contextType, contextId, endpoints, heartbeatIntervalMs }) {
    const key = `${contextType}:${contextId}`;
    let client = registry.get(key);
    // A closed client (401/403/410) stays closed; a fresh acquire starts over with a new session.
    if (client && client.terminalReason) {
      client.dispose();
      client = null;
    }
    if (!client || client.disposed) {
      client = new RoomClient({ key, contextType, contextId, endpoints, heartbeatIntervalMs });
      registry.set(key, client);
    }
    return new RoomHandle(client);
  },
};
